import React, { ReactElement, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import useAddToCart, { AddToCartButtonState } from './useAddToCart';

import { isInSandboxMode } from '../../helpers';
import { trans } from '../../helpers/translations';

import { widgetActivityTracking } from '../../store/actions';
import { trackingEvents } from '../../store/activityTrackingHelpers';
import tooltipStyles from '../Tooltip/Tooltip.styles';
import Translation from '../Translation';
import { InfoIcon, NewSkinLoader, SuccessCheck } from '../svgIcons/SvgIcons';
import styles from './AddToCartButton.styles';

interface AddToCartButtonViewProps {
    buttonState: AddToCartButtonState;
    hasError?: boolean;
    onAddToCartClick: () => void;
    onViewCartClick: () => void;
    disabled?: boolean;
}

export const AddToCartButtonView = ({
    buttonState,
    hasError = false,
    onAddToCartClick,
    onViewCartClick,
    disabled = false,
}: AddToCartButtonViewProps): ReactElement | null => {
    const isSandboxMode = isInSandboxMode();

    if (buttonState === 'add') {
        return (
            <button
                type="button"
                disabled={disabled}
                onClick={onAddToCartClick}
                css={styles.button}
            >
                <Translation value="recommendProductAddToCart" fallback="Add to cart" />
            </button>
        );
    }
    if (buttonState === 'adding') {
        return (
            <button type="button" disabled css={styles.addingToCartButton}>
                <NewSkinLoader width={20} height={20} />

                <Translation value="recommendProductAddingToCart" fallback="Adding to cart" />
            </button>
        );
    }

    if (buttonState === 'added') {
        return (
            <button type="button" disabled css={styles.addedToCartButton}>
                <SuccessCheck />
                <Translation value="recommendProductAdded" fallback="Added" />
            </button>
        );
    }

    if (buttonState === 'cart') {
        const isViewCartDisabled = disabled || isSandboxMode;
        return (
            <>
                <button
                    type="button"
                    disabled={isViewCartDisabled}
                    onClick={onViewCartClick}
                    css={[styles.button, tooltipStyles.tooltipButton({ showOnDisabled: true })]}
                >
                    <Translation value="recommendProductViewCart" fallback="View cart" />
                    {isSandboxMode && (
                        <span className="tooltip" css={styles.viewCartPlaygroundTooltip}>
                            {trans(
                                'viewCartActionNotAvailableInPlayground',
                                null,
                                "View cart action isn't available in Lyro playground.",
                            )}
                        </span>
                    )}
                </button>
                {hasError && (
                    <div css={styles.errorMessageContainer}>
                        <InfoIcon />
                        <p css={styles.errorMessage}>
                            {trans(
                                'recommendProductAddToCartError',
                                null,
                                "Couldn't add to cart. Open the product page to continue.",
                            )}
                        </p>
                    </div>
                )}
            </>
        );
    }

    return null;
};

interface AddToCartButtonProps {
    displayedVariantId: number;
    messageId: string;
    disabled?: boolean;
    onAddToCart: () => Promise<boolean>;
    isFlyMessage?: boolean;
}

const AddToCartButton = ({
    displayedVariantId,
    messageId,
    disabled,
    onAddToCart,
    isFlyMessage,
}: AddToCartButtonProps): ReactElement | null => {
    const dispatch = useDispatch();

    const onViewCartClick = useCallback(() => {
        dispatch(widgetActivityTracking(trackingEvents.productRecommendationViewCartClicked));
    }, [dispatch]);

    const { buttonState, hasError, executeAddToCart, handleViewCart } = useAddToCart({
        displayedVariantId,
        messageId,
        isFlyMessage,
        onAddToCart,
        onViewCartClick,
    });

    const handleAddToCartClick = useCallback(() => {
        executeAddToCart(displayedVariantId);
    }, [executeAddToCart, displayedVariantId]);

    return (
        <AddToCartButtonView
            buttonState={buttonState}
            hasError={hasError}
            onAddToCartClick={handleAddToCartClick}
            onViewCartClick={handleViewCart}
            disabled={disabled}
        />
    );
};

export default AddToCartButton;
